/**
 * Lecteur YouTube encapsulé (react-youtube). Gère le démarrage au bon décalage (startSeconds),
 * le mute, et s’enregistre dans le contexte pour la correction de synchronisation (App).
 */
import React, {useContext, useRef, useEffect, useCallback} from 'react';
import YouTube from 'react-youtube';
import {PlayerContext} from './App';

/** Écart max (ms) pour considérer currentTime égal à startSeconds. */
const TIME_EPSILON_MS = 80;
/** Délai court (ms) après PLAYING avant seek + unmute pour laisser le lecteur stabiliser. */
const SEEK_AFTER_PLAYING_MS = 150;

/** État PLAYING de l'API YouTube IFrame. */
const YT_PLAYING = 1;

function VideoPlayer({url, startSeconds, play, mute, player, quality}) {
    const {playerReadyList, setPlayerReadyList, globalCurrentTimeRef, registerPlayer, unregisterPlayer} = useContext(PlayerContext);
    const playerRef = useRef(null);
    /** true tant qu’on attend le premier seek après play (onStateChange PLAYING). */
    const pendingInitialSeekRef = useRef(false);

    // Extraire l'ID de la vidéo YouTube à partir de l'URL
    const videoId = getYouTubeID(url);

    // Options pour le lecteur YouTube
    const opts = {
        width: '100%',
        height: '100%',
        playerVars: {
            // https://developers.google.com/youtube/player_parameters
            autoplay: play ? 1 : 0,
            start: startSeconds,
            mute: mute ? 1 : 0,
            controls: 0,
            modestbranding: 1,
            rel: 0,
            disablekb: 1,
            fs: 0,
            iv_load_policy: 3,
            loop: 1,
            showinfo: 0,
            cc_load_policy: 0,
            color: 'white',
            logo: 0,
        },
    };

    // Fonction pour extraire l'ID de la vidéo YouTube à partir de l'URL
    function getYouTubeID(url) {
        const regExp = /^.*(youtu.be\/|v\/|e\/|u\/\w+\/|embed\/|v=)([^#&?]*).*/;
        const match = url.match(regExp);
        if (match && match[2].length === 11) {
            return match[2];
        } else {
            // Gérer l'erreur ou retourner une valeur par défaut
            return url;
        }
    }

    /** Au chargement du player IFrame : enregistrement pour la sync, et signal "prêt" dans le contexte. */
    const onReady = (event) => {
        playerRef.current = event.target;
        registerPlayer(player, playerRef, startSeconds);
        if (playerReadyList.length === 0 || !playerReadyList.includes(player)) {
            setPlayerReadyList([...playerReadyList, player]);
        }
    };

    // Nettoyage : désenregistrer ce lecteur au démontage
    useEffect(() => {
        return () => unregisterPlayer(player);
    }, [player, unregisterPlayer]);

    // Réenregistrer avec le nouveau startSeconds si il change
    useEffect(() => {
        if (playerRef.current) registerPlayer(player, playerRef, startSeconds);
    }, [startSeconds, registerPlayer, player]);

    /** Seek + unmute + masquer loaders une fois le lecteur en état PLAYING (évite le délai fixe 5s). */
    const applyInitialSeek = useCallback(() => {
        if (!playerRef.current) return;
        const seekToValue = startSeconds < 0 ? startSeconds - 0.099 : startSeconds;
        if (!mute) playerRef.current.unMute();
        playerRef.current.seekTo(seekToValue, true);
        document.querySelectorAll('.nh').forEach(el => el.style.opacity = '0');
        setTimeout(() => {
            document.querySelectorAll('.tv, .bv').forEach(el => {
                el.style.transition = 'opacity 1s ease-out';
                el.style.opacity = '0';
            });
        }, 3000);
    }, [startSeconds, mute]);

    /** Au passage en PLAYING : si on attend le seek initial, on l’exécute après un court délai. */
    const onStateChange = useCallback((event) => {
        if (event.data === YT_PLAYING && pendingInitialSeekRef.current) {
            pendingInitialSeekRef.current = false;
            setTimeout(applyInitialSeek, SEEK_AFTER_PLAYING_MS);
        }
    }, [applyInitialSeek]);

    /** Démarrage depuis le début : affiche loaders, lance la lecture (et seek initial via onStateChange). */
    function playVideoAtStart(delay) {
        document.querySelectorAll('.nh').forEach(el => el.style.opacity = '1');
        pendingInitialSeekRef.current = true;
        setTimeout(() => {
            if (!playerRef.current) return;
            if (!mute) playerRef.current.mute();
            playerRef.current.playVideo();
            playerRef.current.setPlaybackQuality(quality);
        }, delay);
    }

    /** Reprise ou démarrage différé : gère le délai négatif (startSeconds < 0) et le cas "proche du début". */
    function playVideoDuring(delayMs, currentTimeMs) {
        const nearStart = Math.abs(currentTimeMs - startSeconds * 1000) < TIME_EPSILON_MS;

        if (delayMs > 0) {
            if (globalCurrentTimeRef.current <= delayMs) {
                setTimeout(() => {
                    if (!playerRef.current) return;
                    pendingInitialSeekRef.current = true;
                    if (!mute) playerRef.current.mute();
                    playerRef.current.playVideo();
                    playerRef.current.setPlaybackQuality(quality);
                }, delayMs - globalCurrentTimeRef.current);
            } else {
                playerRef.current.playVideo();
            }
        } else {
            if (nearStart) {
                document.querySelectorAll('.nh').forEach(el => el.style.opacity = '1');
                pendingInitialSeekRef.current = true;
                if (!mute && playerRef.current) playerRef.current.mute();
                playerRef.current.playVideo();
                playerRef.current.setPlaybackQuality(quality);
            } else {
                playerRef.current.playVideo();
            }
        }
        setTimeout(() => {
            document.querySelectorAll('.tv, .bv').forEach(el => el.style.opacity = '0');
        }, 100);
    }

    /** Met en pause ; si ce lecteur a un délai (startSeconds < 0), pause différée selon l’horloge maître. */
    function stopVideo(delay) {
        if (delay > 0) {
            if (globalCurrentTimeRef.current <= delay) {
                setTimeout(function () {
                    playerRef.current.pauseVideo();
                }, delay - globalCurrentTimeRef.current);
            } else {
                playerRef.current.pauseVideo();
            }
        } else {
            playerRef.current.pauseVideo();
        }
        document.querySelectorAll('.tv, .bv').forEach(el => el.style.opacity = '1');
    }

    // Effet pour gérer la lecture et l'arrêt de la vidéo (déclenché uniquement au toggle play).
    useEffect(() => {
        if (!playerRef.current) return;

        let currentTimeSec = playerRef.current.getCurrentTime();
        if (startSeconds > 0 && currentTimeSec < 0.001) {
            currentTimeSec = startSeconds;
        }
        const currentTimeMs = currentTimeSec * 1000;
        const delayMs = startSeconds < 0 ? -startSeconds * 1000 : 0;

        document.querySelectorAll('.tv, .bv').forEach(el => el.style.transition = 'opacity 0s');
        if (play) {
            const atStart = currentTimeMs < TIME_EPSILON_MS || Math.abs(currentTimeMs - startSeconds * 1000) < TIME_EPSILON_MS;
            if (atStart && delayMs === 0) {
                playVideoAtStart(0);
            } else if (atStart && delayMs > 0) {
                playVideoAtStart(delayMs);
            } else {
                playVideoDuring(delayMs, currentTimeMs);
            }
        } else {
            pendingInitialSeekRef.current = false;
            stopVideo(delayMs);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- on purpose: run only when play toggles
    }, [play]);

    return (
        <YouTube
            videoId={videoId}
            opts={opts}
            onReady={onReady}
            onStateChange={onStateChange}
            className="h-full"
        />
    );
}

export default VideoPlayer;
